// src/components/AdBanner.tsx
// Google AdSense ad banner component for free users

import { useEffect, useRef } from "react";
import { useAuthStore, useUIStore } from "@/stores";

declare global {
  interface Window {
    adsbygoogle: unknown[];
  }
}

const ADSENSE_CLIENT_ID = "ca-pub-5548788230831376";
let adsenseScriptLoaded = false;
let adsenseScriptLoading = false;

// Export for testing - allows resetting module state between tests
export function __resetAdsenseState() {
  adsenseScriptLoaded = false;
  adsenseScriptLoading = false;
}

// Dynamically load AdSense script only when needed (when content is present)
function loadAdsenseScript(): Promise<void> {
  if (adsenseScriptLoaded) return Promise.resolve();
  if (adsenseScriptLoading) {
    // Wait for existing load to complete
    return new Promise((resolve) => {
      const checkLoaded = setInterval(() => {
        if (adsenseScriptLoaded) {
          clearInterval(checkLoaded);
          resolve();
        }
      }, 100);
    });
  }

  adsenseScriptLoading = true;
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = () => {
      adsenseScriptLoaded = true;
      adsenseScriptLoading = false;
      resolve();
    };
    script.onerror = () => {
      adsenseScriptLoading = false;
      reject(new Error("Failed to load AdSense script"));
    };
    document.head.appendChild(script);
  });
}

interface AdBannerProps {
  slot: string;
  format?: "auto" | "rectangle" | "horizontal" | "vertical";
  responsive?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export function AdBanner({
  slot,
  format = "auto",
  responsive = true,
  className = "",
  style,
}: AdBannerProps) {
  const adRef = useRef<HTMLModElement>(null);
  const { user } = useAuthStore();
  const { studySessionActive, pageHasContent } = useUIStore();

  // Check if user is on pro plan by checking profile
  // For now we check if they have a profile with plan = 'pro'
  const isPro = (user as { plan?: string } | null)?.plan === "pro";

  useEffect(() => {
    // Don't show ads for Pro users, during study sessions, or on low-content pages
    if (isPro || studySessionActive || !pageHasContent) return;

    let cancelled = false;

    // Load AdSense script dynamically, then push ad
    const initAd = async () => {
      try {
        await loadAdsenseScript();
        if (cancelled) return;

        // Wait a bit for container to have size
        await new Promise((resolve) => setTimeout(resolve, 100));
        if (cancelled) return;

        if (typeof window !== "undefined" && window.adsbygoogle && adRef.current) {
          // Only push if the container has width (is visible)
          const container = adRef.current.parentElement;
          if (container && container.offsetWidth > 0) {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
          }
        }
      } catch (error) {
        // Silently ignore AdSense errors - common during development
        if (import.meta.env.DEV) {
          console.log("AdSense not ready yet");
        }
      }
    };

    initAd();

    return () => {
      cancelled = true;
    };
  }, [isPro, studySessionActive, pageHasContent]);

  // Don't render anything for Pro users, during study sessions, or on low-content pages
  if (isPro || studySessionActive || !pageHasContent) {
    return null;
  }

  return (
    <div className={`ad-container relative ${className}`} style={style}>
      {/* Fallback message shown when ad blocker is active */}
      <div
        className="absolute inset-0 flex items-center justify-center bg-background-tertiary border border-border-subtle rounded-lg p-4 text-center"
        style={style}
      >
        <p className="text-xs text-foreground-muted">
          Please disable your ad blocker to help support Koda. Ads help us keep this service running.
        </p>
      </div>
      {/* Ad overlays the fallback message when loaded */}
      <ins
        ref={adRef}
        className="adsbygoogle relative z-10"
        style={{
          display: "block",
          ...style,
        }}
        data-ad-client="ca-pub-5548788230831376"
        data-ad-slot={slot}
        data-ad-format={format}
        data-full-width-responsive={responsive ? "true" : "false"}
      />
      <p className="text-xs text-foreground-muted text-center mt-1 relative z-10">
        Advertisement
      </p>
    </div>
  );
}

// Sidebar ad (300x250 medium rectangle)
export function SidebarAd() {
  const { user } = useAuthStore();
  const { studySessionActive, pageHasContent } = useUIStore();
  const isPro = (user as { plan?: string } | null)?.plan === "pro";

  if (isPro || studySessionActive || !pageHasContent) {
    return null;
  }

  return (
    <div className="mt-4 mb-2">
      <AdBanner
        slot="2605587155"
        format="rectangle"
        responsive={false}
        style={{ width: 234, height: 250 }}
      />
    </div>
  );
}

// Mobile bottom banner
export function MobileBottomAd() {
  const { user } = useAuthStore();
  const { studySessionActive, pageHasContent } = useUIStore();
  const isPro = (user as { plan?: string } | null)?.plan === "pro";

  if (isPro || studySessionActive || !pageHasContent) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-background-secondary border-t border-border-subtle lg:hidden safe-area-bottom">
      <AdBanner
        slot="6353260476"
        format="horizontal"
        responsive={true}
        style={{ width: "100%", height: 50 }}
      />
    </div>
  );
}
